import { h, render, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { memo } from 'preact/compat'
import { useSize } from '@utils/dom'
import { redirect } from '@utils/misc'
import translate from '@core/translate'

function MegaDropdownItem({ id, url, cssClass, seName, title, subItems, onItemClick, ...rest }) {
	return (
		<div
			role="link"
			tabIndex="-1"
			className={`mega-menu-dropdown__item ${cssClass}`}
			key={id}
			onClick={e => {
				if (!subItems.length > 0) {
					redirect(url || seName)
				}

				e.stopPropagation()
				e.preventDefault()
				onItemClick({ id, subItems })
			}}
		>
			{url || seName ? <a href={url || seName}>{title}</a> : <span>{title}</span>}

			{subItems.length > 0 && (
				<svg
					width="24"
					height="24"
					fill="none"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M8.293 4.293a1 1 0 0 0 0 1.414L14.586 12l-6.293 6.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414l-7-7a1 1 0 0 0-1.414 0Z"
						fill="#000000"
					/>
				</svg>
			)}
		</div>
	)
}

function MegaDropdown({ items, cssClass, isSubLevel, onClose, parentTitle, parentUrl }) {
	const [dropdownItems, setDropdownItems] = useState([])
	const [itemClicked, setItemClicked] = useState({
		title: '',
		url: '',
	})

	const handleItemClick = ({ title, url, subItems }) => {
		setDropdownItems(subItems)
		setItemClicked({
			title,
			url,
		})
	}

	const handleOnClose = () => {
		setDropdownItems([])
	}

	useEffect(() => {
		setDropdownItems([])
	}, [items])

	return (
		<div className={`mega-menu-dropdown ${cssClass}`}>
			<div className="mega-menu-dropdown__wrapper">
				{isSubLevel && (
					<button
						type="button"
						aria-label="Stäng underkategori"
						className="close"
						onClick={onClose}
					>
						<svg
							width="28"
							height="28"
							viewBox="0 0 28 28"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M3.14905 3.3874L3.26256 3.26256C3.90578 2.61935 4.92513 2.58151 5.6126 3.14905L5.73744 3.26256L14 11.525L22.2626 3.26256C22.946 2.57915 24.054 2.57915 24.7374 3.26256C25.4209 3.94598 25.4209 5.05402 24.7374 5.73744L16.475 14L24.7374 22.2626C25.3807 22.9058 25.4185 23.9251 24.8509 24.6126L24.7374 24.7374C24.0942 25.3807 23.0749 25.4185 22.3874 24.8509L22.2626 24.7374L14 16.475L5.73744 24.7374C5.05402 25.4209 3.94598 25.4209 3.26256 24.7374C2.57915 24.054 2.57915 22.946 3.26256 22.2626L11.525 14L3.26256 5.73744C2.61935 5.09422 2.58151 4.07487 3.14905 3.3874L3.26256 3.26256L3.14905 3.3874Z"
								fill="#212121"
							/>
						</svg>
					</button>
				)}

				{parentTitle && parentUrl && (
					<a href={parentUrl} className="mega-menu-dropdown__item --highlighted --view-all">
						{translate('menu.viewall.titled', parentTitle)}
					</a>
				)}

				{items &&
					items.length > 0 &&
					items.map(item => (
						<MegaDropdownItem
							{...item}
							key={item.id}
							onItemClick={() =>
								handleItemClick({
									subItems: item.subItems,
									title: item.title,
									url: item.url ?? item.seName,
								})
							}
						/>
					))}

				{dropdownItems && dropdownItems.length > 0 && (
					<MegaDropdown
						isSubLevel={true}
						items={dropdownItems}
						parentTitle={itemClicked.title}
						parentUrl={itemClicked.url}
						onClose={handleOnClose ?? null}
					/>
				)}
			</div>
		</div>
	)
}

function MegaMenuItem({
	id,
	openInNewWindow,
	subItems,
	title,
	type,
	seName,
	url,
	cssClass,
	active,

	onResetChevrons,
	onToggleItem,
	onSetCssClass,
	...rest
}) {
	const handleClick = e => {
		// follow link
		if (!subItems.length > 0) {
			redirect(url || seName)
			return
		}

		e.preventDefault()

		// Unset active on other items
		onResetChevrons()

		// Tilt the chevron down for this item
		e.target.parentElement.classList.add('active')

		// Swap out sub categories
		onToggleItem({
			subItems,
			cssClass,
		})
	}

	return (
		<li className={`mega-menu__categories__item ${cssClass}`} onClick={handleClick}>
			{url || seName ? (
				// eslint-disable-next-line react/jsx-no-target-blank
				<a
					onClick={e => e.preventDefault()}
					href={url || seName}
					rel={openInNewWindow ? 'noreferrer' : ''}
					target={openInNewWindow ? '_blank' : '_self'}
				>
					{title}

					{subItems?.length > 0 && (
						<svg
							className="topmenu-catalog-chevron"
							width="18"
							height="18"
							viewBox="0 0 48 48"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M8.36612 16.1161C7.87796 16.6043 7.87796 17.3957 8.36612 17.8839L23.1161 32.6339C23.6043 33.122 24.3957 33.122 24.8839 32.6339L39.6339 17.8839C40.122 17.3957 40.122 16.6043 39.6339 16.1161C39.1457 15.628 38.3543 15.628 37.8661 16.1161L24 29.9822L10.1339 16.1161C9.64573 15.628 8.85427 15.628 8.36612 16.1161Z"
								fill="#212121"
							></path>
						</svg>
					)}
				</a>
			) : (
				<Fragment>
					<span>{title}</span>

					{subItems?.length > 0 && (
						<svg
							className="topmenu-catalog-chevron"
							width="18"
							height="18"
							viewBox="0 0 48 48"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M8.36612 16.1161C7.87796 16.6043 7.87796 17.3957 8.36612 17.8839L23.1161 32.6339C23.6043 33.122 24.3957 33.122 24.8839 32.6339L39.6339 17.8839C40.122 17.3957 40.122 16.6043 39.6339 16.1161C39.1457 15.628 38.3543 15.628 37.8661 16.1161L24 29.9822L10.1339 16.1161C9.64573 15.628 8.85427 15.628 8.36612 16.1161Z"
								fill="#212121"
							></path>
						</svg>
					)}
				</Fragment>
			)}
		</li>
	)
}

function MegaMenu({ isAuthenticated, routes }) {
	const [offset, setOffset] = useState(16)

	// initial data from server side render
	const megaMenu = window.CRITICAL_STATE.menu.megaMenus[0]

	// elements to use
	const backdrop = document.querySelector('.modal-overlay')

	// component state
	const [dropdownItems, setDropdownItems] = useState([])
	const [currentCssClass, setCurrentCssClass] = useState('')

	// sets correct chevron rotation
	const resetChevrons = () => {
		const topLevelMenuItems = Array.from(document.querySelectorAll('.mega-menu__categories > li'))

		if (topLevelMenuItems.length > 0) {
			topLevelMenuItems.map(li => li.classList.toggle('active', false))
		}
	}

	// clear state
	const clear = () => {
		setDropdownItems([])
		resetChevrons()
	}

	// on mount interactions
	useEffect(() => {
		backdrop.addEventListener('click', clear)

		// reset state when user presses the back button in the browser
		window.addEventListener('pageshow', () => {
			clear()
		})
	}, [])

	const handleToggleMenuItem = item => {
		// The user clicked the same menu item twice
		if (item.subItems === dropdownItems) {
			setDropdownItems([])
			resetChevrons()
			window._drawerManager.closeAll()

			return
		}

		// Set css class
		setCurrentCssClass(item.cssClass)

		// Set component state
		setDropdownItems(item.subItems)

		// Close other drawers
		window._drawerManager.closeAllExcept('menu')

		// Open menu drawer
		window._drawerManager.open('menu')
	}

	const accountLinkStyles = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginLeft: 'auto',
	}

	return (
		<nav className="mega-menu">
			<ul className={`mega-menu__categories ${currentCssClass}`}>
				<div
					id="modal-menu"
					data-drawer-name="menu"
					data-animate-in="fade-in"
					data-animate-out="fade-out"
					data-title="Main Menu"
					className="hidden"
				>
					{dropdownItems && dropdownItems.length > 0 && (
						<MegaDropdown items={dropdownItems} cssClass={currentCssClass} offset={offset} />
					)}
				</div>

				{megaMenu.items.map(item => (
					<MegaMenuItem
						{...item}
						key={item.id}
						active={dropdownItems == item.subItems}
						onToggleItem={handleToggleMenuItem}
						onResetChevrons={resetChevrons}
					/>
				))}

				<div style={accountLinkStyles}>
					<a className="mega-menu__categories__item" href={routes.customer.register}>
						Bli kund
					</a>
					{/* {isAuthenticated ? (
						<>
							<a className="mega-menu__categories__item" href={routes.customer.info}>
								Mina sidor
							</a>
							<a className="mega-menu__categories__item" href={routes.customer.logout}>
								Logga ut
							</a>
						</>
					) : (
						<>
							<a className="mega-menu__categories__item" href={routes.customer.login}>
								Logga in
							</a>
							<a className="mega-menu__categories__item" href={routes.customer.register}>
								Bli kund
							</a>
						</>
					)} */}
				</div>
			</ul>
		</nav>
	)
}

export default memo(MegaMenu)
